/**
 * Axios interceptor for automatic JWT token refresh.
 * On 401, attempts to refresh via the refresh-token cookie,
 * updates localStorage, then retries the original request.
 */
import axios from 'axios';

axios.defaults.xsrfCookieName = 'csrftoken';
axios.defaults.xsrfHeaderName = 'X-CSRFToken';
axios.defaults.withCredentials = true;

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only handle 401 and avoid infinite retry loops
    if (
      error.response?.status !== 401 ||
      originalRequest._retry ||
      originalRequest.url?.includes('/api/auth/token/refresh/')
    ) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // Queue this request until the refresh completes
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers['Authorization'] = 'Bearer ' + token;
        return axios(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      // Refresh token is in the refresh-token cookie — sent automatically
      const response = await axios.post(
        '/api/auth/token/refresh/',
        {},
        { withCredentials: true }
      );

      const newAccessToken = response.data.access;

      // Update stored token
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        user.access = newAccessToken;
        localStorage.setItem('user', JSON.stringify(user));
      }

      axios.defaults.headers.common['Authorization'] = 'Bearer ' + newAccessToken;
      originalRequest.headers['Authorization'] = 'Bearer ' + newAccessToken;

      processQueue(null, newAccessToken);
      return axios(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      // Refresh failed — clear all auth state (localStorage + cookies) and redirect to login
      localStorage.removeItem('user');
      // JWT cookies are non-httponly (JWT_AUTH_HTTPONLY=False) so we can clear them here.
      // Without this, stale cookies keep being sent even after localStorage is cleared,
      // causing a 401 loop after a DB wipe or server-side session invalidation.
      ['auth-token', 'refresh-token', 'sessionid', 'csrftoken'].forEach(name => {
        document.cookie = `${name}=; Max-Age=0; path=/`;
      });
      window.location.href = '/login';
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);
